import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { CircleX, Info, ScanSearch, TriangleAlert } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { notify } from '../utils/notify';

const PARAMS = ['forfait_percentage', 'forfait_max_amount', 'tax_free_amount', 'aov_percentage'];

// Simulation of the year's Loonbelasting parameters as they are on screen
// (saved or not) on the last approved payroll of that year: what each guard
// was approved with and what the parameters would give. Read-only: the
// server recalculates and saves nothing.
const TaxYearSimulation = ({ year, draft }) => {
  const { t, locale } = useLanguage();
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  // A result belongs to the year it was run for.
  useEffect(() => { setResult(null); }, [year]);

  const srd = (n) => `SRD ${Number(n).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const signed = (n) => `${n > 0 ? '+' : ''}${srd(n)}`;

  const run = async () => {
    if (PARAMS.some((key) => draft[key] === '' || draft[key] === null || draft[key] === undefined || !Number.isFinite(Number(draft[key])))) {
      notify(t('Fill in every amount before simulating'), 'error');
      return;
    }
    try {
      setRunning(true);
      const body = Object.fromEntries(PARAMS.map((key) => [key, Number(draft[key])]));
      const response = await api.post(`/settings/loonbelasting/${year}/simulate`, body);
      setResult(response.data);
    } catch (error) {
      notify(error.response?.data?.message || t('Error simulating the tax year'), 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="card tc-panel tc-sim">
      <h3 className="tc-panel__title"><ScanSearch aria-hidden="true" /> {t('Simulation on the last approved payroll')}</h3>
      <p>{t('Recalculates the last approved payroll of {year} with the parameters above, saved or not, and compares it with what was approved. Nothing is saved.', { year })}</p>
      <button type="button" className="btn btn--primary" onClick={run} disabled={running}>
        {running ? t('Calculating...') : t('Simulate')}
      </button>

      {result && !result.period && (
        <div className="tc-alert" role="status">
          <Info aria-hidden="true" /> {t('There is no approved payroll for {year} yet, so there is nothing to compare with. The simulation will be available once a payroll of {year} is approved.', { year })}
        </div>
      )}

      {result && result.period && (
        <>
          <p className="tc-muted">
            {t('Payroll of {start} to {end}: {count} guards', { start: result.period.period_start, end: result.period.period_end, count: result.employees.length })}
          </p>
          {result.employees.some((e) => e.gross_changed) && (
            <div className="tc-alert" role="note">
              <TriangleAlert aria-hidden="true" /> {t('Marked ⚠️: the gross recalculated today is not the approved one (attendance or rates changed since), so the difference is not only the parameters.')}
            </div>
          )}
          <div className="tc-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('Employee')}</th>
                  <th className="num">{t('LB approved')}</th><th className="num">{t('LB new')}</th>
                  <th className="num">{t('AOV approved')}</th><th className="num">{t('AOV new')}</th>
                  <th className="num">{t('Net approved')}</th><th className="num">{t('Net new')}</th>
                  <th className="num">{t('Difference')}</th>
                </tr>
              </thead>
              <tbody>
                {result.employees.map((e) => (
                  <tr key={e.payroll_id}>
                    <td>{e.gross_changed && <span title={t('The gross recalculated today is not the approved one')}>⚠️ </span>}{e.name} <small className="field-hint">{e.employee_code}</small></td>
                    <td className="num">{srd(e.approved.lb)}</td>
                    {e.simulated ? (
                      <>
                        <td className="num">{srd(e.simulated.lb)}</td>
                        <td className="num">{srd(e.approved.aov)}</td><td className="num">{srd(e.simulated.aov)}</td>
                        <td className="num">{srd(e.approved.net)}</td><td className="num">{srd(e.simulated.net)}</td>
                        <td className="num"><strong>{signed(e.difference)}</strong></td>
                      </>
                    ) : (
                      <td colSpan="6">❌ {e.error}</td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>{t('Total')}</th>
                  <th className="num">{srd(result.totals.approved.lb)}</th><th className="num">{srd(result.totals.simulated.lb)}</th>
                  <th className="num">{srd(result.totals.approved.aov)}</th><th className="num">{srd(result.totals.simulated.aov)}</th>
                  <th className="num">{srd(result.totals.approved.net)}</th><th className="num">{srd(result.totals.simulated.net)}</th>
                  <th className="num">{signed(result.totals.difference)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
          {result.totals.errors > 0 && (
            <p className="tc-muted"><CircleX aria-hidden="true" /> {t('{count} payrolls could not be recalculated and are not in the totals.', { count: result.totals.errors })}</p>
          )}
        </>
      )}
    </div>
  );
};

export default TaxYearSimulation;
